<script>
import { Cropper, RectangleStencil } from 'vue-advanced-cropper';
import 'vue-advanced-cropper/dist/style.css';


export default {
  name: 'ImageCropper',
  components: { Cropper },
  props: {
    src: {
      type: String,
      required: true,
    },
    aspectRatio: {
      type: Number,
      default: null,
    },
  },
  emits: ['update', 'cancel'],
  data() {
    return {
      stencilComponent: RectangleStencil,
      isCropping: false,
      cropResult: null,
      isSaving: false,
    };
  },
  computed: {
    imageRestriction() {
      return this.aspectRatio !== null ? 'stencil' : 'none';
    },
    stencilProps() {
      const defaults = {
        minWidth:    10,    // 10% of image width
        minHeight:   10,    // 10% of image height
        // default to a box that covers 50% of image
        defaultSize: { width: 50, height: 50 },
      };
      if (this.aspectRatio !== null) {
        // ratio‐locked crop (e.g. 1:1)
        return {
          ...defaults,
          aspectRatio:    this.aspectRatio,
          minAspectRatio: this.aspectRatio,
          maxAspectRatio: this.aspectRatio,
        };
      }
      // free‐form crop
      return defaults;
    },
  },
  methods: {
    startCropping() {
      this.isCropping = true;
      this.cropResult = null;
    },
    cancelCropping() {
      this.isCropping = false;
      this.cropResult = null;
      this.isSaving = false;
    },
    onCrop({ canvas }) {
      this.cropResult = canvas;
    },
    saveOriginal() {
      if (this.isSaving) return;
      this.isSaving = true;
      this.$emit('update', this.src);
      this.isSaving = false;
    },
    saveCropped() {
      if (this.isSaving) return;
      this.isSaving = true;
      this.isCropping = false;
      this.$emit('update', this.cropResult ? this.cropResult.toDataURL() : this.src);
      this.isSaving = false;
    },
  },
};
</script>

<template>
  <div class="overflow-hidden w-full max-w-lg mx-auto">
    <!-- Display Mode -->
    <div
      v-if="!isCropping"
      class="p-4 flex flex-col items-center"
    >
      <img
        :src="src"
        alt="To be cropped"
        class="w-full h-auto rounded-md border border-gray-200"
      >
      <div class="mt-4 flex space-x-3">
        <button
          class="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition"
          @click="$emit('cancel')"
        >
          Cancel
        </button>
        <button
          class="px-4 py-2 rounded-md  transition crop-btn"
          @click="startCropping"
        >
          Crop
        </button>
        <button
          :disabled="isSaving"
          class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          @click="saveOriginal"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>

    <!-- Cropping Mode -->
    <div v-else>
      <!-- Header -->
      <div class="relative px-6 py-4 border-b">
        <h2 class="text-lg font-semibold text-gray-800">
          Crop Image
        </h2>
      </div>

      <!-- Cropper Area -->
      <div class="p-4">
        <cropper
          :src="src"
          :stencil-component="stencilComponent"
          :stencil-props="stencilProps"
          :image-restriction="imageRestriction"
          :auto-zoom="false"
          :resize-image="true"
          @change="onCrop"
        />
      </div>

      <!-- Actions -->
      <div class="px-6 py-4 bg-gray-50 flex justify-end space-x-3">
        <button
          class="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition"
          @click="cancelCropping"
        >
          Cancel
        </button>
        <button
          :disabled="isSaving"
          class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition disabled:opacity-50 disabled:cursor-not-allowed"
          @click="saveCropped"
        >
          {{ isSaving ? 'Saving...' : 'Save' }}
        </button>
      </div>
    </div>
  </div>
</template>

<style scoped>
.crop-btn {
  @apply bg-default_secondary_dark text-white;
}
</style>
